import { World } from './world';
import { BuildingDescription, building_description, BuildingType, Building, cost_for_building } from './building';
import { Road, cost_for_road } from './road';
import { JsonDictionaryWrapper } from './utils';
import { Purse, resource_name } from './resources';
import { WorldPoint } from './grid';
import { Command, CommandViability } from './command';

type BuildTool = {
    kind: 'BuildTool'
    building: BuildingType
}

type RoadTool = {
    kind: 'RoadTool'
}

type NoTool = {
    kind: 'NoTool'
}

type Tool = BuildTool | RoadTool | NoTool

enum TownTool {
    None,
    Road,
}

function building_from_tool(tool: Tool): BuildingType | null {
    if (tool.kind == 'BuildTool') {
        return tool.building
    }

    return null
}

/*
  Single button

  This will call the passed action when it is clicked to select
 */
class ToolbarButton {
    private button: HTMLElement

    constructor(root: HTMLElement, title: string, subtitles: string[], blk: (bt: ToolbarButton) => void) {
        this.button = document.createElement("div")
       
        root.append(this.button)
        this.button.classList.add("game-tool-bar-button")

        var title_el = document.createElement("div")
        this.button.append(title_el)
        title_el.innerHTML = title
        title_el.classList.add("button-title")

        subtitles.forEach(st => {
            var subtitle_el = document.createElement("div")
            this.button.append(subtitle_el)
            subtitle_el.innerHTML = st
            subtitle_el.classList.add("button-subtitle")
        })

        this.button.addEventListener('click', (e) => {
            e.preventDefault()
            blk(this)
        }, false);
    }

    public get state(): boolean {
        return this.button.classList.contains("button-selected")
    }

    public set state(val: boolean) {
        if (val == this.state) {
            return
        }

        if (val) {
            this.button.classList.add("button-selected")
        } else {
            this.button.classList.remove("button-selected")
        }
    }
}

export class ToolButtons<SelectedType> {
    private buttons: ToolbarButton[] = []
    private _value: SelectedType | null = null
    private values: (SelectedType | null)[]
    private callback: () => void
    private root_element: HTMLElement
    private describer: ((ty: SelectedType) => string[])

    constructor(root_id: string, callback: (() => void), describer: ((ty: SelectedType) => string[])) {
        this.callback = callback
        this.describer = describer
        this.values = []

        var root = document.getElementById(root_id)
        if (root == null) {
            throw new Error("failed to find main tool bar")
        }
        this.root_element = root
    }

    public get visible(): boolean {
        return this.root_element.style.display == "inline-block"
    }

    public set visible(visible: boolean) {
        if (visible) {
            this.root_element.style.display = "inline-block"
        } else {
            this.root_element.style.display = "none"
        }
    }

    public add_tool_button(tool: SelectedType, title: string) {
        const subtitles = this.describer(tool)
        var btn = new ToolbarButton(this.root_element, title, subtitles, (bt: ToolbarButton) => {
            bt.state = true
            this.value = tool
            this.callback()
        })
        this.buttons.push(btn)
        this.values.push(tool)

        if (this.buttons.length == 1) {
            this.value = tool
        }
    }

    public add_function_button(title: string, action: () => void) {
        var btn = new ToolbarButton(this.root_element, title, [ "No refund" ], (bt: ToolbarButton) => {
            action()
        })
        this.buttons.push(btn)
        this.values.push(null)
    }

    public set value(val: SelectedType) {
        if (val == this._value) {
            return
        }

        this._value = val
        for (var i = 0; i < this.values.length; i += 1) {
            this.buttons[i].state = this.values[i] == val
        }
    }

    public get value(): SelectedType {
        if (this._value == null) {
            throw new Error("ToolButtons._value has never been set")
        } else {
            return this._value
        }
    }
}

function cost_for_tool(tool: Tool): Purse {
    var bt = building_from_tool(tool)
    if (bt != null) {
        return cost_for_building(bt)
    }

    if (tool.kind == 'RoadTool') {
        return cost_for_road()
    }

    return new Purse(false)
}

export type WorldSelectionBuilding = {
    kind: 'Building'
    building: Building
}

export type WorldSelectionRoad = {
    kind: 'Road'
    road: Road
}

export type WorldSelection = WorldSelectionBuilding | WorldSelectionRoad | null

/*
  The HTML sourced UI, ie the layer above the canvas 
 */
export class UI {
    private tool_buttons: ToolButtons<Tool>
    private town_buttons: ToolButtons<Tool>
    private road_buttons: ToolButtons<Tool>
    private road_toolbar: HTMLElement
    private road_toolbar_title: HTMLElement
    private town_toolbar: HTMLElement
    private town_toolbar_title: HTMLElement
    private town_toolbar_purse: HTMLElement
    private town_toolbar_progress: HTMLElement
    private status: HTMLElement
    private _wave_status: HTMLElement
    private _selection: WorldSelection = null
    private _hovered_point: WorldPoint | null = null
    private world: World
    private _pending_command: Command | null = null
    private _paused: boolean = false

    constructor(w: World, cmd_drain: (cmd: Command) => void) {
        this.tool_buttons = new ToolButtons<Tool>("main-tool-bar", () => { this.selection = null }, (tool: Tool) => { return cost_for_tool(tool).cost_lines() })
        this.town_buttons = new ToolButtons<Tool>("town-tool-bar-buttons", () => { }, (val: Tool) => { return [ "" + val ]})
        this.road_buttons = new ToolButtons<Tool>("road-tool-bar-buttons", () => { }, (tool: Tool) => {
            if (tool.kind == 'RoadTool') {
                return cost_for_road().cost_lines()
            } else {
                return [ ]
            }
        })

        this.town_toolbar = document.getElementById("town-tool-bar") as HTMLElement;
        this.town_toolbar_title = document.getElementById("town-tool-bar-title") as HTMLElement;
        this.town_toolbar_purse = document.getElementById("town-tool-bar-purse") as HTMLElement;
        this.town_toolbar_progress = document.getElementById("town-tool-bar-formula") as HTMLElement;

        this.road_toolbar = document.getElementById("road-tool-bar") as HTMLElement;
        this.road_toolbar_title = document.getElementById("road-tool-bar-title") as HTMLElement;

        this.world = w

        const required_building_types: BuildingType[] = [
            BuildingType.Mine,
            BuildingType.Refinery,
            BuildingType.PavingStoneFactory,
            BuildingType.Turret,
            BuildingType.Flag,
        ]

        this.tool_buttons.add_tool_button({ kind: 'NoTool' }, "None")
        for (var bt of required_building_types) {
            const bd: BuildingDescription = building_description(bt)
            this.tool_buttons.add_tool_button({ kind: 'BuildTool', building: bt }, bd.name)
        }
        this.tool_buttons.value = { kind: 'NoTool' }

        this.road_buttons.add_function_button("Delete", () => {
            if (this._selection != null && this._selection.kind == 'Road') {
                cmd_drain({
                    cmd: {
                        kind: 'DeleteRoad',
                        start: this._selection.road.source.position,
                        finish: this._selection.road.destination.position,
                    },
                    cost: new Purse(false),
                    viability: CommandViability.Ok,
                })
            } else {
                throw new Error("no selected building when delete pressed")
            }
        })

        this.town_buttons.add_tool_button({ kind: 'NoTool' }, "None")
        this.town_buttons.add_tool_button({ kind: 'RoadTool' }, "Road")
        this.town_buttons.add_function_button("Delete", () => {
            if (this._selection != null && this._selection.kind == 'Building') {
                cmd_drain({
                    cmd: {
                        kind: 'DeleteBuilding',
                        building: this._selection.building.position,
                    },
                    cost: new Purse(false),
                    viability: CommandViability.Ok,
                })
            } else {
                throw new Error("no selected building when delete pressed")
            }
        })
        this.town_buttons.value = { kind: 'NoTool' }
        this.status = document.getElementById("status-display") as HTMLElement
        this._wave_status = document.getElementById("wave-status") as HTMLElement
        this._wave_status.addEventListener('click', e => {
            const c: Command = {
                cmd: { kind: 'SendWave', },
                cost: new Purse(false),
                viability: CommandViability.Ok,
            }
            cmd_drain(c)
        });

        // TODO these need an indication of what is active
        (document.getElementById("pause-button") as HTMLElement).addEventListener('click', e => {
            this.pause_clicked()
        });
        (document.getElementById("play-button") as HTMLElement).addEventListener('click', e => {
            this.play_clicked()
        });

        (document.getElementById("save-button") as HTMLElement).addEventListener('click', e => {
            this.save_clicked()
        });
        // TODO preserve this button and grey it out if there is no save available
        (document.getElementById("load-button") as HTMLElement).addEventListener('click', e => {
            this.load_clicked()
        });

        document.addEventListener('keypress', (e: KeyboardEvent) => this.key_pressed(e.keyCode));

        this.clear_all_selections()

        this.status.innerHTML = this.world.money.description()
        this.world.money.did_change = (purse: Purse) => {
            this.status.innerHTML = purse.description()
        }
    }

    private key_pressed(code: number): void {
        switch (code) {
            case 32:
                this._paused = !this._paused
                break;
        }
    }

    private pause_clicked(): void {
        this._paused = true
    }

    private play_clicked(): void {
        this._paused = false
    }

    public get paused(): boolean {
        return this._paused
    }

    private save_clicked(): void {
        const saved = JSON.stringify(this.world)
        localStorage.setItem("save", saved)
    }

    private load_clicked(): void {
        this.clear_all_selections()
        const save = localStorage.getItem("save")
        if (save == null) {
            return;
        }
        this.world.load_json(JsonDictionaryWrapper.create_from_string(save))
    }

    private selected_build_tool(): BuildingType | null {
        if (!this.tool_buttons.visible) {
            return null
        }
        return building_from_tool(this.tool_buttons.value)
    }

    public set_wave_status(level: number, timing: number): void {
        const seconds = Math.floor(timing / 60)
        this._wave_status.innerHTML = `Wave ${level} in ${seconds} seconds`
    }

    public get hovered_point(): WorldPoint | null {
        return this._hovered_point
    }

    public set hovered_point(wp: WorldPoint | null) {
        if (this._hovered_point == wp) {
            return
        }
        this._hovered_point = wp

        this.update_pending_command()
    }

    public get road_tool(): boolean {
        return this.town_toolbar.style.display == "inline-block" && this.town_buttons.value.kind == 'RoadTool'
    }

    public get selection(): WorldSelection {
        return this._selection
    }

    public set selection(sel: WorldSelection) {
        if (this._selection != null) {
            switch (this._selection.kind) {
                case 'Building': {
                    this._selection.building.purse.did_change = null
                    this._selection.building.did_change = null
                    break
                }
            }
        }

        this._selection = sel
        this.tool_buttons.visible = sel == null

        this.town_toolbar.style.display = "none";
        this.road_toolbar.style.display = "none";

        if (this._selection != null) {
            switch (this._selection.kind) {
                case 'Road': {
                    let rd = this._selection.road
                    this.road_toolbar.style.display = "inline-block";
                    this.road_toolbar_title.innerHTML = "Selected road";
                    break
                }

                case 'Building': {
                    let bld = this._selection.building

                    let tui = this
                    let fn = function() {
                        if (bld.deleted) {
                            // TODO this is a bit grim, should exe command at a level that can clear UI?
                            tui.clear_all_selections()
                            return
                        }

                        let fs = bld.formula_state
                        if (fs != null) {
                            tui.town_toolbar_progress.style.display = "block";
                            let input = ""
                            if (fs.formula.input != null) {
                                input = resource_name(fs.formula.input)
                            }
                            let output = resource_name(fs.formula.output(bld.underlying_resource))
                            tui.town_toolbar_progress.innerHTML = `${input} -> ${output}: ${fs.t} / ${fs.formula.duration}`;
                        } else {
                            tui.town_toolbar_progress.style.display = "none";
                        }
                    }

                    this.town_toolbar.style.display = "inline-block";
                    tui.town_toolbar_title.innerHTML = bld.description.name;
                    tui.town_toolbar_purse.innerHTML = bld.purse.description();
                    fn()
                    bld.purse.did_change = (purse: Purse) => {
                        this.town_toolbar_purse.innerHTML = purse.description();
                    }
                    bld.did_change = fn
                    break
                }
            }
        }
        this.update_pending_command()
    }

    public clear_all_selections(): void {
        this.tool_buttons.value = { kind: 'NoTool' }
        this.town_buttons.value = { kind: 'NoTool' }
        this.selection = null
        this.update_pending_command()
    }

    private update_pending_command(): void {
        this._pending_command = null

        const bt = this.selected_build_tool()
        if (bt != null && this._hovered_point != null) {
            let tiles = (new Building(0, bt, this._hovered_point)).occupied_tiles()
            let res = this.world.mineable_for_footprint(tiles)

            this._pending_command = {
                cmd: {
                    kind: 'CreateBuilding',
                    building_type: bt,
                    position: this._hovered_point,
                },
                cost: new Purse(false), // placing is free
                viability: CommandViability.Ok,
            }

            if (!this.world.can_add_building(bt, this._hovered_point)) {
                this._pending_command.viability = CommandViability.Conflict
            } else if (bt == BuildingType.Mine && res == null) {
                this._pending_command.viability = CommandViability.NoResource
            }
        } else if (this.road_tool && this._hovered_point != null) {
            if (this._selection == null || this._selection.kind != 'Building') {
                throw new Error("no building selected")
            }
            const sb = this._selection.building

            if (this.world.can_build_road(sb.position, this._hovered_point)) {
                const target_building = this.world.find_building(this._hovered_point)
                if (target_building != null) {
                    const r = this.world.find_road(sb, target_building)
                    if (r != null) {
                        this._pending_command = {
                            cmd: {
                                kind: 'CreateRoad',
                                road: r,
                            },
                            cost: cost_for_road(),
                            viability: CommandViability.Ok,
                        }
                    }
                }
            }
        }

        if (this._pending_command != null && this._pending_command.viability == CommandViability.Ok) {
            if (this._pending_command.cmd.kind == 'CreateRoad' && this._pending_command.cmd.road.length > Road.max_length) {
                this._pending_command.viability = CommandViability.TooLong
            } else  if (!this.world.can_afford(this._pending_command.cost)) {
                this._pending_command.viability = CommandViability.Unaffordable
            }
        }
    }

    public get pending_command(): Command | null {
        return this._pending_command 
    }

    // return true if we handle this event, false if it is just a click on the map
    public clicked_coord(wp: WorldPoint): boolean {
        if (this.selection != null) {
            this.clear_all_selections()
        }

        const fb = this.world.find_building(wp)
        if (fb != null) {
            this.clear_all_selections()
            this.selection = {
                kind: 'Building',
                building: fb,
            }
            return true
        }

        const fr = this.world.find_road_at(wp)
        if (fr != null) {
            this.clear_all_selections()
            this.selection = {
                kind: 'Road',
                road: fr,
            }
            return true
        }

        return false
    }
}

